// client/src/Components/DunningBanner/DunningBanner.tsx
//
// Account-wide dunning banner (F15). Shown by DashboardLayout whenever any
// stream subscription is past_due. Per-stream detail (deadline, single
// retry) lives in SubscriptionPanel on the stream page.
import { Button, notification } from "antd";
import { useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState, useAppDispatch, useAppSelector } from "../../redux/store";
import {
  getSubscriptions,
  openBillingPortal,
  retryAllPayments,
  setRetryAllResult,
} from "../../redux/Slices/BillingSlice";
import { streamContentName } from "../../helpers/streamContent";
import { getActiveLiveStreams } from "../../redux/Slices/LiveStreamSlice";
import { SeriousAlert } from "../SeriousAlert/SeriousAlert";
import "./DunningBanner.scss";

const EMPTY_STREAMS: RootState["liveStream"]["activeLiveStreams"] = [];

export const DunningBanner = () => {
  const { t } = useTranslation("billing");
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { subscriptions, retryAllLoading, portalLoading } = useAppSelector(
    (s) => s.billing
  );
  const retryResult = useAppSelector((s) => s.billing.retryAllResult);
  const cachedStreams = useAppSelector((s) => s.liveStream?.activeLiveStreams);
  const streams = cachedStreams ?? EMPTY_STREAMS;
  const pastDue = subscriptions.filter((s) => s.status === "past_due");
  const mounted = useRef(true);
  const requestedNames = useRef(new Set<string>());
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (pastDue.length === 0 && retryResult) dispatch(setRetryAllResult(null));
  }, [pastDue.length, retryResult, dispatch]);
  useEffect(() => {
    const missing = subscriptions
      .filter((subscription) => subscription.status === "past_due")
      .map((subscription) => subscription.liveStreamId)
      .filter((id) => !streams.some((stream) => stream.id === id));
    if (missing.some((id) => !requestedNames.current.has(id))) {
      missing.forEach((id) => requestedNames.current.add(id));
      void dispatch(getActiveLiveStreams(true));
    }
  }, [subscriptions, streams, dispatch]);

  if (pastDue.length === 0) return null;

  const streamName = (id: string) => {
    const stream = streams.find((s: { id: string }) => s.id === id);
    const name = stream ? streamContentName(stream) : "";
    return name && name !== "-" ? name : id.slice(0, 8);
  };

  const onRetryAll = async () => {
    const result = await dispatch(retryAllPayments());
    if (retryAllPayments.fulfilled.match(result)) {
      notification.info({
        message: t("dunning.retryAllResult", {
          attempted: result.payload.attempted,
          paid: result.payload.paid,
        }),
      });
      for (let i = 0; i < 3 && result.payload.paid > 0; i++) {
        if (
          !store
            .getState()
            .billing.subscriptions.some((s) => s.status === "past_due")
        )
          break;
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (!mounted.current) break;
        if (
          !store
            .getState()
            .billing.subscriptions.some((s) => s.status === "past_due")
        )
          break;
        const refreshed = await dispatch(getSubscriptions());
        if (
          getSubscriptions.fulfilled.match(refreshed) &&
          !refreshed.payload.some((s) => s.status === "past_due")
        )
          break;
      }
    }
  };
  const onPortal = async () => {
    const result = await dispatch(openBillingPortal());
    if (openBillingPortal.fulfilled.match(result)) {
      window.location.href = result.payload.url;
    }
  };

  // Serious: money. Retry all is the recovery action (inverse); Update card is a plain button.
  return (
    <SeriousAlert
      className="st-dunning-banner"
      title={
        pastDue.length === 1
          ? t("dunning.bannerOne")
          : t("dunning.bannerMany", { count: pastDue.length })
      }
      actions={
        <>
          <Button loading={portalLoading} onClick={onPortal}>
            {t("dunning.updateCard")}
          </Button>
          <Button
            className="st-btn-inverse"
            loading={retryAllLoading}
            onClick={onRetryAll}
          >
            {t("dunning.retryAll")}
          </Button>
        </>
      }
    >
      <p className="st-dunning-banner__text">{t("dunning.bannerDescription")}</p>
      {retryResult && retryResult.results.length > 0 ? (
        <ul className="st-dunning-banner__outcomes" role="list">
          {retryResult.results.map((result) => (
            <li key={result.liveStreamId}>
              {streamName(result.liveStreamId)}:{" "}
              {t(`dunning.shortOutcome.${result.outcome}`)}
            </li>
          ))}
        </ul>
      ) : null}
    </SeriousAlert>
  );
};

export default DunningBanner;
